/**
 * [PM-SG GIF 2026-10-07, user] WHICH GIF PLAYS BEHIND WHICH NOTIFIER CARD.
 *
 * The user's rule: "in random order the gif will play but make sure firstly all the gif will
 * cover and then again it will get repeated". So this is a shuffled DECK, not a dice roll: every
 * GIF is dealt once, in a random order, before any of them comes round again. A dice roll would
 * show one GIF three times in a week and another never.
 *
 * - When the deck runs out it is reshuffled, and the new round never STARTS with the GIF that
 *   ended the last one. Otherwise the seam between two rounds can play one GIF twice running,
 *   which is exactly the repeat the rule exists to stop.
 * - A GIF belongs to an ANNOUNCEMENT (its id), not to a visit of the page. NotifierView unmounts
 *   whenever the board leaves the page, and anybody can reopen NOTIFIER from the dock to look at
 *   the last card again: that is the same news, so it gets the same GIF and does not use up the
 *   next one.
 * - The deck is kept in localStorage, so the board's own deploy-reload carries on with the round
 *   it was dealing instead of starting a fresh order with some GIFs never shown. Storage is a
 *   convenience only: if it throws (private window, blocked site data) the copy in this module
 *   still keeps the rule for as long as the page stays open.
 *
 * ⚠ THE FILES are `client/public/notifier-gifs/<slug>.webp`, converted from the user's own GIFs
 * (the `local-only/pm-singapore/Gifs/` folder [ROOT-TIDY 2026-10-26], never committed). To add one: put the file there and
 * add its slug below — it joins the deck at the next reshuffle.
 * ⚠ GIF_VERSION is part of every URL and MUST be bumped whenever a file is added, swapped or
 * re-encoded. Prod sits behind Cloudflare with a four-hour cache and answers an unknown path with
 * the SPA's index.html, so a URL requested before its file is deployed caches HTML for four hours
 * — the same trap that once silenced beats.mp3 (see SRC in sound.ts).
 */

export const NOTIFIER_GIFS: readonly string[] = [
  '01-cookie-monster',
  '02-las-vegas',
  '03-excited-nph',
  '04-go-team-ducks',
  '05-howard-dean',
  '06-jim-carrey',
  '07-late-night',
  '08-star-trek',
  '09-the-office',
  '10-tom-cruise',
  '11-snl-dance',
  '12-make-it-rain',
  '13-panda-dance',
  '14-office-party',
  '15-tonight-show-yes',
  '16-victory-champion',
  '17-minions',
  '18-patrick-win',
];

const GIF_VERSION = 1;
const STORE_KEY = 'pmsg-gif-deck';

export function gifUrl(slug: string): string {
  return `/notifier-gifs/${slug}.webp?v=${GIF_VERSION}`;
}

interface Deck {
  /** GIFs still to be dealt in this round, in the order they will play. */
  queue: string[];
  /** The newest announcement given a GIF, and which one it got. */
  last: { id: number; slug: string } | null;
}

let deck: Deck | null = null;

/** A fresh random round of every GIF, never opening on `avoidFirst`. */
function shuffled(avoidFirst: string | null): string[] {
  const list = [...NOTIFIER_GIFS];
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  if (list.length > 1 && list[0] === avoidFirst) {
    const k = 1 + Math.floor(Math.random() * (list.length - 1));
    [list[0], list[k]] = [list[k], list[0]];
  }
  return list;
}

/**
 * The stored deck, cleaned against the current list: a GIF that has since been removed is
 * dropped, a duplicate is dropped, and a GIF added in a later deploy simply joins the next round.
 */
function read(): Deck {
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORE_KEY) || 'null');
    const known = new Set(NOTIFIER_GIFS);
    const queue: string[] = Array.isArray(raw?.queue)
      ? raw.queue.filter((s: unknown, i: number, all: unknown[]) =>
        typeof s === 'string' && known.has(s) && all.indexOf(s) === i)
      : [];
    const last = raw?.last && typeof raw.last.id === 'number' && known.has(raw.last.slug)
      ? { id: raw.last.id as number, slug: raw.last.slug as string }
      : null;
    return { queue, last };
  } catch {
    return { queue: [], last: null };
  }
}

function write(d: Deck): void {
  try { window.localStorage.setItem(STORE_KEY, JSON.stringify(d)); }
  catch { /* the module copy still holds the deck for this page's lifetime */ }
}

function current(): Deck {
  if (!deck) deck = read();
  return deck;
}

/** Deal a new round once this one is used up, so the next GIF is always the head of the queue. */
function topUp(d: Deck): void {
  if (!d.queue.length) d.queue = shuffled(d.last ? d.last.slug : null);
}

/** The GIF for one announcement: the same id always gets the same GIF; a new id deals the next. */
export function gifForAnnouncement(id: number): string {
  const d = current();
  if (d.last && d.last.id === id) return d.last.slug;
  topUp(d);
  const slug = d.queue.shift() as string;
  d.last = { id, slug };
  write(d);
  return slug;
}

/* Held so the request is not dropped with the element before it completes. */
let warming: HTMLImageElement | null = null;

/**
 * Fetch the GIF the NEXT announcement will get, so it is already in the browser's cache when the
 * card opens rather than fading in a second or two late. The biggest file is 6.5 MB.
 */
export function warmNextGif(): void {
  const d = current();
  topUp(d);
  write(d);
  const slug = d.queue[0];
  if (!slug) return;
  const url = gifUrl(slug);
  if (warming && warming.getAttribute('src') === url) return;
  warming = new Image();
  warming.decoding = 'async';
  warming.src = url;
}
